import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ADD_TRANSACTION } from '../../graphql/mutations/transactions';
import { usePortfolioContext } from '../../contexts/PortfolioContext';
import { usePositionContext } from '../../contexts/PositionContext';
import { normalizeDecimalInput, parseFloatFromString } from '../../utils/formatters';
import { SUPPORTED_CURRENCIES } from '../../utils/currencies';
import { toast } from 'react-hot-toast';
import AllocationLocationSelector from './AllocationLocationSelector';

const AllocationQuickBuyModal = ({
  isOpen,
  onClose,
  token,
  amountValue,
  targetLocation,
  locations = [],
  onCreateTargetLocation = null,
  onRenameTargetLocation = null,
  onDeleteTargetLocation = null,
}) => {
  const { currentPortfolioId, currentPortfolio } = usePortfolioContext();
  const [addTransaction, { loading }] = useMutation(ADD_TRANSACTION);
  const { reloadPositions } = usePositionContext();
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [timestamp, setTimestamp] = useState('');
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [fees, setFees] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [priceError, setPriceError] = useState('');
  const [quantityError, setQuantityError] = useState('');
  const [costError, setCostError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const amt = parseFloatFromString(amountValue) || 0;
    const cur = currentPortfolio?.currency || 'USD';
    setCurrency(cur);
    // Do not default quantity; leave empty
    setQuantity('');
    setPrice('');
    setCost(amt.toString());
    setFees('');
    setNotes('');
    setSelectedLocation(targetLocation || null);
    setShowLocationPicker(false);
    setPriceError('');
    setQuantityError('');
    setCostError('');
    // default timestamp now in local datetime-local format
    try {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const ts = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
      setTimestamp(ts);
    } catch (_) {
      setTimestamp('');
    }
  }, [isOpen, amountValue, currentPortfolio?.currency, targetLocation]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen || !token) return null;

  const handleRenameLocation = async (locationId, name) => {
    if (!onRenameTargetLocation) return false;
    const renamed = await onRenameTargetLocation(locationId, name);
    if (renamed !== false && selectedLocation?.id === locationId) {
      setSelectedLocation({ ...selectedLocation, name: (name || '').trim() });
    }
    return renamed;
  };

  const handleAdd = async () => {
    try {
      setPriceError('');
      setQuantityError('');
      setCostError('');

      let numericPrice = parseFloatFromString(price) || 0;
      let numericQty = parseFloatFromString(quantity) || 0;
      let numericCost = parseFloatFromString(cost) || 0;
      // If price and cost are present but quantity is empty, derive quantity
      if (numericQty <= 0 && numericPrice > 0 && numericCost > 0) {
        numericQty = numericCost / numericPrice;
        setQuantity(numericQty.toString());
      }
      // If quantity and cost are present but price is empty, derive price
      if (numericPrice <= 0 && numericQty > 0 && numericCost > 0) {
        numericPrice = numericCost / numericQty;
        setPrice(numericPrice.toString());
      }
      // If price and quantity are present but cost is empty, derive cost (for UI only)
      if (numericCost <= 0 && numericPrice > 0 && numericQty > 0) {
        numericCost = numericPrice * numericQty;
        setCost(numericCost.toString());
      }
      if (!currentPortfolioId || !token.id || numericPrice <= 0 || numericQty <= 0) {
        if (numericPrice <= 0) {
          setPriceError('Enter price or set cost and quantity');
        }
        if (numericQty <= 0) {
          setQuantityError('Enter quantity or set cost and price');
        }
        toast.error('Missing data to add transaction');
        return;
      }
      const nowIso = new Date().toISOString();
      const variables = {
        portfolioId: currentPortfolioId,
        tokenId: token.id,
        transactionType: 'BUY',
        price: price ? normalizeDecimalInput(price) : numericPrice.toString(),
        quantity: quantity ? normalizeDecimalInput(quantity) : numericQty.toString(),
        timestamp: timestamp ? new Date(timestamp).toISOString() : nowIso,
        notes: notes || '',
        currency: currency || 'USD'
      };
      if (selectedLocation?.id) {
        variables.locationId = selectedLocation.id;
      }
      if (fees && parseFloatFromString(fees) > 0) {
        variables.fees = normalizeDecimalInput(fees.toString());
      }
      const res = await addTransaction({ variables });
      const ok = res?.data?.addTransaction?.success;
      if (ok) {
        toast.success('Transaction added');
        try { reloadPositions?.(); } catch (_) {}
        try { window.dispatchEvent(new CustomEvent('allocation:refresh-breakdowns')); } catch (_) {}
        onClose?.();
      } else {
        const err = res?.data?.addTransaction?.error || 'Failed to add transaction';
        toast.error(err);
      }
    } catch (e) {
      toast.error('Error adding transaction');
    }
  };

  const onChangePrice = (val) => {
    setPrice(val);
    if (priceError) setPriceError('');
    const p = parseFloatFromString(val) || 0;
    const q = parseFloatFromString(quantity) || 0;
    if (q > 0) {
      setCost((p * q).toString());
    }
  };

  const onChangeQuantity = (val) => {
    setQuantity(val);
    if (quantityError) setQuantityError('');
    const q = parseFloatFromString(val) || 0;
    const c = parseFloatFromString(cost) || 0;
    // When user edits quantity: if cost is set -> price is calculated
    if (c > 0 && q > 0) {
      setPrice((c / q).toString());
    }
  };

  const onChangeCost = (val) => {
    setCost(val);
    if (costError) setCostError('');
    const c = parseFloatFromString(val) || 0;
    const q = parseFloatFromString(quantity) || 0;
    if (q > 0) {
      setPrice((c / q).toString());
    }
  };

  const numericKeyDown = (e) => {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Enter'];
    if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
    if (/^[0-9]$/.test(e.key)) return;
    if (e.key === '.' || e.key === ',') {
      const v = e.currentTarget.value || '';
      if (v.includes('.') || v.includes(',')) {
        e.preventDefault();
      }
      return;
    }
    e.preventDefault();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Add transaction</h3>
        <div className="mb-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">Location</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {selectedLocation?.name ? (
                  <>Using <span className="font-medium text-gray-800 dark:text-gray-200">{selectedLocation.name}</span>{targetLocation?.id === selectedLocation.id ? ' (planned)' : ''}</>
                ) : (
                  'No location selected'
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowLocationPicker(!showLocationPicker)}
              className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {showLocationPicker ? 'Hide' : 'Change'}
            </button>
          </div>
          {showLocationPicker && (
            <div className="mt-3">
              <AllocationLocationSelector
                locations={locations}
                selectedLocationId={selectedLocation?.id || ''}
                onSelect={(locationId, location) => {
                  setSelectedLocation(locationId ? (location || locations.find(l => l.id === locationId) || null) : null);
                }}
                onCreate={onCreateTargetLocation}
                onRename={handleRenameLocation}
                onDelete={onDeleteTargetLocation}
                autoFocus
              />
            </div>
          )}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); handleAdd(); }}>
        <div className="space-y-3 mb-4">
          {/* Cost with Currency (like full TransactionModal BUY) */}
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Cost</label>
            <div className="flex items-end gap-2">
              <input
                type="text"
                value={cost}
                onChange={(e) => onChangeCost(e.target.value)}
                onKeyDown={numericKeyDown}
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded flex-1 py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 focus:outline-none"
                placeholder="0.00"
              />
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-24 py-2 px-2 text-gray-700 dark:text-gray-300 dark:bg-gray-700 focus:outline-none text-sm"
                aria-label="Currency"
              >
                {SUPPORTED_CURRENCIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {costError && <div className="mt-1 text-xs text-red-600">{costError}</div>}
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Quantity</label>
            <input
              type="text"
              value={quantity}
              onChange={(e) => onChangeQuantity(e.target.value)}
              onKeyDown={numericKeyDown}
              inputMode="decimal"
              pattern="[0-9]*[.,]?[0-9]*"
              className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 focus:outline-none"
              placeholder="1.00"
            />
            {quantityError && <div className="mt-1 text-xs text-red-600">{quantityError}</div>}
          </div>

          {/* Price with Current link */}
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Price</label>
            <div className="relative">
              <input
                type="text"
                value={price}
                onChange={(e) => onChangePrice(e.target.value)}
                onKeyDown={numericKeyDown}
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 pr-16 pl-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 focus:outline-none"
                placeholder="0.00"
              />
              {(() => {
                if (parseFloatFromString(price) > 0) return null;
                // Use portfolio-scaled current price provided by backend
                const current = parseFloatFromString(token?.marketData?.currentPrice) || 0;
                if (current <= 0) return null;
                return (
                  <button
                    type="button"
                    onClick={() => onChangePrice(String(current))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                    title="Use current price"
                  >
                    Current
                  </button>
                );
              })()}
            </div>
            {priceError && <div className="mt-1 text-xs text-red-600">{priceError}</div>}
          </div>

          {/* Date and Time */}
          <div>
            <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Date and Time</label>
            <input
              type="datetime-local"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 focus:outline-none"
            />
          </div>

          {/* Optional Fields Section */}
          <div className="mb-1">
            <button
              type="button"
              onClick={() => setShowOptionalFields(!showOptionalFields)}
              className="flex items-center w-full py-1 px-0 text-left text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
            >
              <span className="font-medium">Fees & Notes (Optional)</span>
              <svg 
                className={`ml-auto h-4 w-4 transform transition-transform ${showOptionalFields ? 'rotate-180' : ''}`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showOptionalFields && (
              <div className="mt-2 space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Fees</label>
                  <input
                    type="text"
                    value={fees}
                    onChange={(e) => setFees(e.target.value)}
                    className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 focus:outline-none"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="shadow appearance-none border border-gray-300 dark:border-gray-600 rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 focus:outline-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Total cost display removed as requested */}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
            disabled={loading}
          >
            Skip
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Adding...' : 'Add'}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default AllocationQuickBuyModal;


